import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    status: 'ONGOING',
    location: '',
    price: '',
    description: '',
    image_url: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('projects').select('*').order('created_at', { ascending: false });
    if (error) {
      console.error('Error fetching projects:', error);
    } else {
      setProjects(data || []);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);

    let finalImageUrl = formData.image_url;

    try {
      // If a new image file is selected, upload it
      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `projects/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('hbb_files')
          .upload(filePath, imageFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('hbb_files')
          .getPublicUrl(filePath);

        finalImageUrl = publicUrl;
      }

      const payload = { ...formData, image_url: finalImageUrl };

      if (editingId) {
        const { error } = await supabase
          .from('projects')
          .update(payload)
          .eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('projects')
          .insert([payload]);
        if (error) throw error;
      }

      setFormData({ title: '', status: 'ONGOING', location: '', price: '', description: '', image_url: '' });
      setImageFile(null);
      setEditingId(null);
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Failed to save project: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = (project) => {
    setFormData({
      title: project.title,
      status: project.status,
      location: project.location || '',
      price: project.price || '',
      description: project.description || '',
      image_url: project.image_url || ''
    });
    setEditingId(project.id);
    setImageFile(null); // Reset any selected file
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      const { error } = await supabase.from('projects').delete().eq('id', id);
      if (error) console.error('Error deleting project:', error);
      fetchProjects();
    }
  };

  return (
    <div className="p-8 min-h-screen bg-[#131313] text-white">
      <h2 className="text-3xl font-bold mb-6 text-[#f59e0b]" style={{ fontFamily: 'Bebas Neue' }}>Manage Projects</h2>
      
      <div className="bg-[#1c1b1b] p-6 rounded-xl border border-white/10 mb-8 shadow-xl">
        <h3 className="text-xl font-semibold mb-4 text-[#d8c3ad]">{editingId ? 'Edit Project' : 'Add New Project'}</h3>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder="Project Title" required className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white" />
          <select name="status" value={formData.status} onChange={handleInputChange} className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white">
            <option value="ONGOING">ONGOING</option>
            <option value="COMPLETED">COMPLETED</option>
          </select>
          <input type="text" name="location" value={formData.location} onChange={handleInputChange} placeholder="Location (e.g. Aspen, Colorado)" className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white" />
          <input type="text" name="price" value={formData.price} onChange={handleInputChange} placeholder="Price (e.g. $28,500,000)" className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white" />
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-400 mb-1">Project Image</label>
            {formData.image_url && !imageFile && (
              <div className="mb-2">
                <img src={formData.image_url} alt="Current project" className="h-20 w-auto rounded object-cover border border-white/10" />
              </div>
            )}
            <input 
              type="file" 
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])} 
              className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white w-full" 
            />
            <p className="text-xs text-gray-500 mt-1">Leave empty to keep existing image</p>
          </div>
          <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Description" rows="3" className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white md:col-span-2" />
          <button type="submit" disabled={processing} className={`md:col-span-2 font-bold py-3 rounded transition-colors flex justify-center items-center gap-2 ${processing ? 'bg-gray-600 text-gray-300 cursor-not-allowed' : 'bg-[#f59e0b] text-[#2a1700] hover:bg-[#ffc174]'}`}>
            {processing ? (
              <><span className="material-symbols-outlined animate-spin text-sm">sync</span> Processing...</>
            ) : editingId ? (
              'Update Project'
            ) : (
              'Save Project'
            )}
          </button>
          {editingId && (
            <button 
              type="button" 
              onClick={() => {
                setEditingId(null);
                setFormData({ title: '', status: 'ONGOING', location: '', price: '', description: '', image_url: '' });
                setImageFile(null);
              }}
              disabled={processing}
              className="md:col-span-2 mt-2 px-6 py-2 bg-transparent border border-white/20 text-white rounded hover:bg-white/5 transition-colors font-bold"
            >
              Cancel
            </button>
          )}
        </form>
      </div>

      <div className="bg-[#1c1b1b] rounded-xl border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[600px]">
            <thead>
              <tr className="border-b border-white/5 bg-white/5">
                <th className="px-6 py-4 font-normal text-[#a08e7a]">Title</th>
                <th className="px-6 py-4 font-normal text-[#a08e7a]">Location</th>
                <th className="px-6 py-4 font-normal text-[#a08e7a]">Status</th>
                <th className="px-6 py-4 font-normal text-[#a08e7a] text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" className="text-center py-8">Loading...</td></tr>
              ) : projects.length === 0 ? (
                <tr><td colSpan="4" className="text-center py-8 text-gray-500">No projects found.</td></tr>
              ) : (
                projects.map(proj => (
                  <tr key={proj.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-6 py-4 font-medium whitespace-nowrap">{proj.title}</td>
                    <td className="px-6 py-4 text-gray-400 whitespace-nowrap">{proj.location || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs ${proj.status === 'ONGOING' ? 'bg-[#f59e0b]/20 text-[#f59e0b]' : 'bg-green-500/20 text-green-500'}`}>
                        {proj.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-3 whitespace-nowrap">
                      <button onClick={() => handleEdit(proj)} className="text-blue-400 hover:text-blue-300">Edit</button>
                      <button onClick={() => handleDelete(proj.id)} className="text-red-400 hover:text-red-300">Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminProjects;
